// MixedFamilyRationalFractionToSinglePowerTask.js
// Zadanie 5 – mieszane czynniki → jedna potęga (3 składniki, bez głównej kreski ułamkowej)

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// liczby równoważne dla podstaw 2,3,5 (do sprawdzania)
const EQUIV = {
  4: { base: 2, power: 2 },
  8: { base: 2, power: 3 },
  9: { base: 3, power: 2 },
  27: { base: 3, power: 3 },
  25: { base: 5, power: 2 },
  125: { base: 5, power: 3 },
};

const FAMILY = {
  2: { nums: [2, 4, 8], mul: { 2: 1, 4: 2, 8: 3 } },
  3: { nums: [3, 9, 27], mul: { 3: 1, 9: 2, 27: 3 } },
  5: { nums: [5, 25, 125], mul: { 5: 1, 25: 2, 125: 3 } },
};

// --- rachunek na ułamkach ---
const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = a % b;
    a = b;
    b = t;
  }
  return a || 1;
};
const lcm = (a, b) => Math.abs(a * b) / gcd(a, b);

function normFrac(n, d) {
  if (d === 0) return { n: 0, d: 1 };
  const s = d < 0 ? -1 : 1;
  const g = gcd(n, d);
  return { n: s * (n / g), d: s * (d / g) };
}

function addFrac(a, b) {
  const D = lcm(a.d, b.d);
  const n = a.n * (D / a.d) + b.n * (D / b.d);
  return normFrac(n, D);
}

const simplifyFraction = (n, d) => normFrac(n, d);

// ładny zapis wykładnika: minus przed ułamkiem
const expLatex = (n, d = 1) => {
  const { n: nn, d: dd } = simplifyFraction(n, d);
  const sign = nn < 0 ? "-" : "";
  const a = Math.abs(nn);
  if (a % dd === 0) return `${sign}${a / dd}`;
  return `${sign}\\tfrac{${a}}{${dd}}`;
};

// czy po skróceniu wykładnik to dokładnie 1
const isExponentOne = (n, d = 1) => {
  const { n: nn, d: dd } = simplifyFraction(n, d);
  return nn === dd; // 1/1
};

/* ---------- Generatory pojedynczych czynników ---------- */

function radicalTerm(famBase) {
  const fam = FAMILY[famBase];
  const num = pick(fam.nums);
  const mul = fam.mul[num];
  const q = pick([2, 3, 4, 5]);
  const p = pick([1, 2, 3]);

  const contrib = normFrac(mul * p, q);
  const inside = p === 1 ? `${num}` : `${num}^{${p}}`;
  const latex = q === 2 ? `\\sqrt{${inside}}` : `\\sqrt[${q}]{${inside}}`;
  return { latex, contrib, meta: { kind: "rad", q, num, p, mul } };
}

// pierwiastek z ułamka – √[q](1/num^p)
function radicalFractionTerm(famBase) {
  const fam = FAMILY[famBase];
  const num = pick(fam.nums);
  const mul = fam.mul[num];
  const q = pick([2, 3, 4, 5]);
  const p = pick([1, 2, 3]);

  const contrib = normFrac(-mul * p, q);
  const pow = `${num}^{-${p}}`; // od razu 25^{-3}
  const latex =
    q === 2 ? `\\sqrt{${pow}}` : `\\sqrt[${q}]{${pow}}`;

  return { latex, contrib, meta: { kind: "radFrac", q, num, p, mul } };
}

function rationalPowerTerm(famBase, { forceNegative = false } = {}) {
  const fam = FAMILY[famBase];
  const num = pick(fam.nums);
  const mul = fam.mul[num];

  const sRaw = pick([2, 3, 4, 5]);
  const rRaw = pick([1, 2, 3, 4]);
  const sign = forceNegative ? -1 : Math.random() < 0.5 ? -1 : 1;

  // skracamy ułamek r/s razem z znakiem
  const frac = normFrac(sign * rRaw, sRaw);
  const n = frac.n; // może być ujemny
  const d = frac.d;
  const absN = Math.abs(n);

  // wkład do sumy wykładników
  const contrib = normFrac(mul * n, d);

  let latex;
  if (absN % d === 0) {
    // wykładnik całkowity, np. -2/2 -> -1, 4/2 -> 2
    const k = absN / d;
    latex = n < 0 ? `${num}^{-${k}}` : `${num}^{${k}}`;
  } else {
    // prawdziwy ułamek po skróceniu, np. 3/6 -> 1/2
    latex =
      n < 0
        ? `${num}^{-\\tfrac{${absN}}{${d}}}`
        : `${num}^{\\tfrac{${absN}}{${d}}}`;
  }

  return {
    latex,
    contrib,
    meta: { kind: "rat", num, mul, r: n, s: d, sign: n < 0 ? -1 : 1 },
  };
}


// a^k / 1/a^k / dzielenie przez a^k
function integerPowerOrFraction(famBase) {
  const fam = FAMILY[famBase];
  const num = pick(fam.nums);
  const mul = fam.mul[num];

  const k = pick([2, 3, 4, 5]); // unikamy ^1 w treści
  const variant = pick(["frac", "pow", "divide"]);

  if (variant === "frac") {
    const latex = `\\dfrac{1}{${num}^{${k}}}`;
    const contrib = normFrac(-mul * k, 1);
    return { latex, contrib, as: "frac", meta: { kind: "frac", num, k, mul } };
  }
  if (variant === "divide") {
    const latex = `${num}^{${k}}`;
    const contrib = normFrac(-mul * k, 1);
    return {
      latex,
      contrib,
      as: "divide",
      meta: { kind: "divide", num, k, mul },
    };
  }
  const latex = `${num}^{${k}}`;
  const contrib = normFrac(mul * k, 1);
  return { latex, contrib, as: "pow", meta: { kind: "pow", num, k, mul } };
}

/* ---------- Generator zadania (tylko wersja „easy”) ---------- */

export function generateTask() {
  const famBase = pick([2, 3, 5]);
  let best = null;

  for (let tries = 0; tries < 80; tries++) {
    const pieces = [];

    // 1) zawsze pierwiastek z ułamka
    pieces.push(radicalFractionTerm(famBase));

    // 2) zwykły pierwiastek albo potęga wymierna
    pieces.push(
      Math.random() < 0.5
        ? radicalTerm(famBase)
        : rationalPowerTerm(famBase)
    );

    // 3) a^k / 1/a^k / dzielenie przez a^k
    pieces.push(integerPowerOrFraction(famBase));

    const K = pieces.reduce(
      (acc, t) => addFrac(acc, t.contrib),
      normFrac(0, 1)
    );

    // preferujemy ujemny, niecałkowity wykładnik
    if (K.n < 0 && K.n % K.d !== 0) {
      best = { pieces, K };
      break;
    }
    if (!best && K.n < 0) {
      best = { pieces, K };
    }
  }

  if (!best) {
    const pieces = [
      radicalFractionTerm(famBase),
      Math.random() < 0.5
        ? radicalTerm(famBase)
        : rationalPowerTerm(famBase),
      integerPowerOrFraction(famBase),
    ];
    best = {
      pieces,
      K: pieces.reduce((acc, t) => addFrac(acc, t.contrib), normFrac(0, 1)),
    };
  }

  const { pieces, K } = best;

  let leftLatex = "";
  for (let i = 0; i < pieces.length; i++) {
    leftLatex += pieces[i].latex;
    if (i < pieces.length - 1) {
      const sep =
        pieces[i + 1].as === "divide" ? "\\,:\\," : "\\,\\cdot\\,";
      leftLatex += sep;
    }
  }

  return {
    type: "MixedFamilyRationalFractionToSinglePowerTask",
    data: { base: famBase, leftLatex, K, pieces },
  };
}

/* ---------- Render / Input ---------- */

export function renderTask({ data }) {
  const { base, leftLatex } = data;
  return (
    <div style={{ marginBottom: "12px" }}>
      <div>
        Przedstaw w postaci jednej potęgi o podstawie{" "}
        <InlineMath math={String(base)} />:
      </div>
      <BlockMath math={leftLatex} />
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const placeholder = "np. 5^2/3 lub 5^-2/3";

  return (
    <div>
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        label=""
        powerPreview={true}
      />
      <div aria-hidden style={{ marginTop: "-16px" }} />
      <div aria-hidden style={{ height: "12px" }} />
    </div>
  );
}


/* ---------- Parsowanie odpowiedzi użytkownika ---------- */

function parseUser(raw) {
  if (!raw) return null;
  const s = String(raw).replace(/\s/g, "");

  // 1) 1/(a^(p/q))
  let m = s.match(/^1\/\((\d+)\^\((-?\d+)\/(\d+)\)\)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const num = -parseInt(m[2], 10);
    const den = parseInt(m[3], 10);
    return { base, exp: normFrac(num, den) };
  }

  // 2) 1/a^(p/q)
  m = s.match(/^1\/(\d+)\^\((-?\d+)\/(\d+)\)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const num = -parseInt(m[2], 10);
    const den = parseInt(m[3], 10);
    return { base, exp: normFrac(num, den) };
  }

  // 3) a^(p/q) lub a^(-p/q)
  m = s.match(/^(\d+)\^\((-?\d+)\/(\d+)\)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const num = parseInt(m[2], 10);
    const den = parseInt(m[3], 10);
    return { base, exp: normFrac(num, den) };
  }

  // 4) a^p/q lub a^-p/q (bez nawiasów)
  m = s.match(/^(\d+)\^\-?(\d+)\/(\d+)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const hasMinus = s.includes("^-");
    const num = hasMinus ? -parseInt(m[2], 10) : parseInt(m[2], 10);
    const den = parseInt(m[3], 10);
    return { base, exp: normFrac(num, den) };
  }

  // 5) 1/a^k
  m = s.match(/^1\/(\d+)\^(\d+)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const k = parseInt(m[2], 10);
    return { base, exp: normFrac(-k, 1) };
  }

  // 6) a^-k lub a^k
  m = s.match(/^(\d+)\^\-?(\d+)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const k = parseInt(m[2], 10);
    const isNeg = s.includes("^-");
    return { base, exp: normFrac(isNeg ? -k : k, 1) };
  }

  return null;
}

function sameFrac(a, b) {
  return a.n * b.d === b.n * a.d;
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw)
    return false;
  const parsed = parseUser(userAnswer.raw);
  if (!parsed) return false;

  // Sprowadzamy ewentualnie bazę 4,8,9,27,25,125 do 2,3,5
  let base = parsed.base;
  let n = parsed.exp.n;
  let d = parsed.exp.d;

  const eq = EQUIV[base];
  if (eq) {
    base = eq.base;
    n = n * eq.power;
  }

  if (base !== data.base) return false;

  const target = simplifyFraction(data.K.n, data.K.d);
  const userExp = simplifyFraction(n, d);

  return sameFrac(target, userExp);
}

/* ---------- Ładny podgląd odpowiedzi ---------- */

function prettyUserLatex(raw) {
  if (!raw) return "";
  const s = String(raw).replace(/\s/g, "");

  // 1/(a^(p/q))
  let m = s.match(/^1\/\((\d+)\^\((-?\d+)\/(\d+)\)\)$/);
  if (m)
    return `\\dfrac{1}{${m[1]}^{\\left(${expLatex(
      -parseInt(m[2], 10),
      parseInt(m[3], 10)
    )}\\right)}}`;

  // 1/a^(p/q)
  m = s.match(/^1\/(\d+)\^\((-?\d+)\/(\d+)\)$/);
  if (m)
    return `\\dfrac{1}{${m[1]}^{${expLatex(
      -parseInt(m[2], 10),
      parseInt(m[3], 10)
    )}}}`;

  // a^(p/q)
  m = s.match(/^(\d+)\^\((-?\d+)\/(\d+)\)$/);
  if (m)
    return `${m[1]}^{${expLatex(
      parseInt(m[2], 10),
      parseInt(m[3], 10)
    )}}`;

  // a^p/q lub a^-p/q
  m = s.match(/^(\d+)\^\-?(\d+)\/(\d+)$/);
  if (m) {
    const num = s.includes("^-") ? -parseInt(m[2], 10) : parseInt(m[2], 10);
    return `${m[1]}^{${expLatex(num, parseInt(m[3], 10))}}`;
  }

  // 1/a^k
  m = s.match(/^1\/(\d+)\^(\d+)$/);
  if (m) return `\\dfrac{1}{${m[1]}^{${m[2]}}}`;

  // a^-k / a^k
  m = s.match(/^(\d+)\^\-?(\d+)$/);
  if (m) {
    const isNeg = s.includes("^-");
    return `${m[1]}^{${isNeg ? "-" : ""}${m[2]}}`;
  }

  return s;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw)
    return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={prettyUserLatex(userAnswer.raw)} />;
}

export function renderAnswer({ data }) {
  const { base, K } = data;
  const e = expLatex(K.n, K.d);
  const one = isExponentOne(K.n, K.d);
  return <InlineMath math={one ? String(base) : `${base}^{${e}}`} />;
}

/* ---------- Wyjaśnienie (tylko wersja „easy”) ---------- */

export function renderExplanation({ data }) {
  const { base, leftLatex, K, pieces } = data;

  const hint =
    base === 2
      ? "4=2^{2},\\;8=2^{3}"
      : base === 3
      ? "9=3^{2},\\;27=3^{3}"
      : "25=5^{2},\\;125=5^{3}";

  const rules =
    "a^{r}\\cdot a^{s}=a^{r+s}\\quad (a^{r})^{t}=a^{r\\cdot t}\\quad \\sqrt[q]{a^{p}}=a^{\\tfrac{p}{q}}\\quad \\dfrac{1}{a^{r}}=a^{-r}";

  // Krok 1 tylko wtedy, gdy naprawdę mamy dzielenie / ułamki 1/a^k
  const hasStep1 = pieces.some(
    (t) => t.as === "divide" || t.as === "frac"
  );

  const transformForStep1 = (t) => {
    if (t.as === "frac" || t.as === "divide") {
      const { num, k } = t.meta;
      return `${num}^{-${k}}`;
    }
    // dla pozostałych (pierwiastki, potęgi) nic nie zmieniamy
    return t.latex;
  };

  let step1Latex = null;

  if (hasStep1) {
    const parts = pieces.map((t, i) =>
      i === 0 ? transformForStep1(t) : `\\,\\cdot\\,${transformForStep1(t)}`
    );
    step1Latex = parts.join("");
  }

  const exps = pieces.map((t) => expLatex(t.contrib.n, t.contrib.d));
  const sumExp = exps.reduce((acc, e, i) => {
    if (i === 0) return e;
    return e.startsWith("-") ? `${acc} ${e}` : `${acc} +${e}`;
  }, "");

  const finalExp = expLatex(K.n, K.d);
  const one = isExponentOne(K.n, K.d);

  return (
    <div style={{ display: "grid", rowGap: "8px" }}>
      {hasStep1 && (
        <>
          <p>
            <strong>Krok 1:</strong> Zamień dzielenie i ułamki na potęgi z
            ujemnymi wykładnikami.
          </p>
          <BlockMath math={`${leftLatex}\\;=\\;${step1Latex}`} />
        </>
      )}

      <p>
        <strong>Krok {hasStep1 ? 2 : 1}:</strong> Zamień wszystkie liczby z
        rodziny na potęgi wspólnej podstawy {base}.
      </p>
      <BlockMath math={hint} />

      <p>
        <strong>Krok {hasStep1 ? 3 : 2}:</strong> Skorzystaj z zasad działań na
        potęgach.
      </p>
      <BlockMath math={rules} />

      <p>
        <strong>Krok {hasStep1 ? 4 : 3}:</strong> Zsumuj wykładniki:
      </p>
      <BlockMath
        math={
          one
            ? `${base}^{${sumExp}}\\;=\\;${base}`
            : `${base}^{${sumExp}}\\;=\\;${base}^{${finalExp}}`
        }
      />
    </div>
  );
}
